import { dlog } from '../utils/debug';
import React, { useState, useEffect, useRef } from 'react';
import './DynamicFlapperUnified.css';
import FaceHalf from './FaceHalf';
import {
  calculatePerformanceEmotion,
  getMostUsedColor,
  inferMyTeam,
  EMOTIONS
} from '../utils/emotionEngine';
import QRCode from 'react-qr-code';

/**
 * DynamicFlapperUnified - Three-mode component: loader, logo, qrcode
 * 
 * MODES:
 * - loader: Infinite flapping animation with neutral colors
 * - logo: Emotional state based on match data, interactive tooltip
 * - qrcode: Scannable QR code revealed with staggered flip
 * 
 * IMPORTANT: Does NOT modify emotion engine or existing logo logic
 */

const DEFAULT_BG_COLOR = '#C4B5A0';

const DynamicFlapperUnified = ({ 
  // Mode configuration
  mode = 'loader', // 'loader' | 'logo' | 'qrcode'
  
  // Data props
  matches = [], // Required for logo mode
  matchId = null, // Required for qrcode mode
  teamA = null, // Required for qrcode mode (for overlay text)
  teamB = null, // Required for qrcode mode (for overlay text)
  
  // State props
  isLoading = false, // Whether data is currently loading
  
  // Display props
  size = 'medium', // 'tiny' | 'small' | 'medium' | 'large'
  showTooltip = true, // For logo mode only
  
  // Loader customization
  loaderBaseColor = '#FFF2DD',
  loaderDarkness1 = 0.10, // 10% darker (level 1)
  loaderDarkness2 = 0.25, // 25% darker (level 2)
  loaderDarkness3 = 0.35  // 35% darker (level 3)
}) => {
  // Helper to calculate initial loader color
  const getInitialLoaderColor = () => {
    const hex = loaderBaseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const darkenFactor = loaderDarkness1;
    const newR = Math.round(r * (1 - darkenFactor));
    const newG = Math.round(g * (1 - darkenFactor));
    const newB = Math.round(b * (1 - darkenFactor));
    const toHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  };

  // Determine initial color based on loading state
  const getInitialColor = () => {
    // If we're loading or in loader mode, start with loader colors
    if (mode === 'loader' || isLoading) {
      return getInitialLoaderColor();
    }
    // Otherwise start with neutral color
    return getInitialLoaderColor();
  };
  
  // State for visible (back) layer - use lazy initialization to avoid recalculating
  const [leftColor, setLeftColor] = useState(() => getInitialColor());
  const [rightColor, setRightColor] = useState(() => getInitialColor());
  const [leftEmotion, setLeftEmotion] = useState('default');
  const [rightEmotion, setRightEmotion] = useState('default');
  
  // State for animating (front) layer - use lazy initialization
  const [leftColorFront, setLeftColorFront] = useState(() => getInitialColor());
  const [rightColorFront, setRightColorFront] = useState(() => getInitialColor());
  const [leftEmotionFront, setLeftEmotionFront] = useState('default');
  const [rightEmotionFront, setRightEmotionFront] = useState('default');
  
  // Animation state
  const [isAnimatingLeft, setIsAnimatingLeft] = useState(false);
  const [isAnimatingRight, setIsAnimatingRight] = useState(false);
  // Initialize isReady: true for pure loader mode, false for logo/qr (will be set when ready)
  const [isReady, setIsReady] = useState(mode === 'loader');
  const [loaderComplete, setLoaderComplete] = useState(false); // Trigger when loader finishes
  
  // Refs for animation tracking
  const prevMatchesRef = useRef(null);
  const animateSide = useRef(true); // Track which side to animate next (true = left, false = right)
  const leftDarknessLevel = useRef(1);
  const rightDarknessLevel = useRef(1);
  const currentCycleLevel = useRef(1); // Track current cycle level (1, 2, or 3)
  const currentFlapInCycle = useRef(0); // Track which flap in current cycle (0 = left, 1 = right)
  const loaderInitialized = useRef(false);
  const noMatchTimeout = useRef(null);
  const hasInitialized = useRef(false);
  const animationTimers = useRef([]);
  const shouldStopLoader = useRef(false); // Flag to stop infinite loader
  const loaderFlipCount = useRef(0); // Track number of flips completed
  const minFlipsBeforeTransition = 2; // Complete at least 2 flips (left + right = one cycle)
  const hasMountedAndChecked = useRef(false); // Track if we've run the initial check
  const hasCompletedLoaderCycle = useRef(false); // Track if loader has run at least once

  // Size configuration
  const sizeConfig = {
    tiny: { dimension: 32, viewBox: 32, featureScale: 0.8, strokeWidth: 4 },
    small: { dimension: 48, viewBox: 48, featureScale: 1.0, strokeWidth: 5 },
    medium: { dimension: 64, viewBox: 64, featureScale: 1.2, strokeWidth: 6 },
    large: { dimension: 80, viewBox: 80, featureScale: 1.4, strokeWidth: 7 }
  };
  const config = sizeConfig[size] || sizeConfig.medium;

  const sizes = {
    tiny: 0.5,
    small: 0.75,
    medium: 1,
    large: 1.25
  };

  const scale = sizes[size] || sizes.medium;
  const dimension = config.dimension * scale;
  
  // Scale up face size for smaller logos to improve readability
  const faceScaleMultiplier = size === 'tiny' ? 1.4 : size === 'small' ? 1.3 : 1;
  const faceSize = (dimension / 2) * faceScaleMultiplier;

  // Function to generate darkened colors for loader
  const generateColor = (darknessLevel) => {
    const hex = loaderBaseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    let darkenFactor;
    if (darknessLevel === 1) {
      darkenFactor = loaderDarkness1;
    } else if (darknessLevel === 2) {
      darkenFactor = loaderDarkness2;
    } else {
      darkenFactor = loaderDarkness3;
    }
    
    const newR = Math.round(r * (1 - darkenFactor));
    const newG = Math.round(g * (1 - darkenFactor));
    const newB = Math.round(b * (1 - darkenFactor));
    
    const toHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  };

  // Helper function to clear all animation timers
  const clearAllAnimationTimers = () => {
    animationTimers.current.forEach(timerId => clearTimeout(timerId));
    animationTimers.current = [];
    dlog('[Unified] 🧹 Cleared all animation timers');
  };


  // Main effect: handles loader, logo, and qrcode modes
  useEffect(() => {
    // ============================================
    // PRIORITY CHECK: Are we in a content display mode (logo or qrcode)?
    // ============================================
    if (mode === 'logo' || mode === 'qrcode') {
      // If still loading, use loader
      if (isLoading) {
        dlog(`[Unified] ⏭️  ${mode} mode: Still loading - using loader...`);
        // Let it fall through to loader mode below
      } else if (!hasCompletedLoaderCycle.current) {
        // Loading complete but loader cycle not finished - continue loader
        dlog(`[Unified] ⏭️  ${mode} mode: Loading complete but loader cycle not finished - continuing loader...`);
        // Let it fall through to loader mode below
      } else {
        // Loader cycle complete - transition to content display
        dlog(`[Unified] ✨ ${mode} mode: Loader cycle complete, transitioning to ${mode}...`);
        clearAllAnimationTimers();
        
        // Mark as ready
        setIsReady(true);
        hasMountedAndChecked.current = true;
        
        // Determine what content to display based on mode
        if (mode === 'qrcode') {
          // QR mode: white background for QR code
          if (!matchId) {
            console.error('[Unified] ⚠️  QR mode: Missing matchId');
            return;
          }
          
          const neutralColor = '#FFFFFF';
          setLeftColorFront(neutralColor);
          setRightColorFront(neutralColor);
          
          // Trigger staggered animation
          const qrTimer1 = setTimeout(() => {
            setIsAnimatingLeft(true);
            
            const qrTimer2 = setTimeout(() => {
              setLeftColor(neutralColor);
              const qrTimer3 = setTimeout(() => {
                setIsAnimatingLeft(false);
              }, 50);
              animationTimers.current.push(qrTimer3);
            }, 800);
            animationTimers.current.push(qrTimer2);
            
            const qrTimer4 = setTimeout(() => {
              setIsAnimatingRight(true);
              
              const qrTimer5 = setTimeout(() => {
                setRightColor(neutralColor);
                const qrTimer6 = setTimeout(() => {
                  setIsAnimatingRight(false);
                }, 50);
                animationTimers.current.push(qrTimer6);
              }, 800);
              animationTimers.current.push(qrTimer5);
            }, 150);
            animationTimers.current.push(qrTimer4);
          }, 50);
          animationTimers.current.push(qrTimer1);
          
          return;
        }
        
        // Logo mode continues below with emotion calculation...
      }
    }
    
    // ============================================
    // PRIORITY CHECK: Is this a loading state?
    // ============================================
    // Always check isLoading FIRST to prevent color flash
    // Also check if loader cycle needs to complete for logo/qrcode modes
    const shouldBeInLoaderMode = mode === 'loader' || isLoading || 
                                ((mode === 'logo' || mode === 'qrcode') && !hasCompletedLoaderCycle.current);
    
    // For logo mode, if we're not loading and haven't completed the cycle, start the transition
    if (mode === 'logo' && !isLoading && !hasCompletedLoaderCycle.current && loaderInitialized.current) {
      dlog('[Unified] 🎯 Logo mode ready - stopping loader after minimum flips');
      shouldStopLoader.current = true;
    }
    
    if (shouldBeInLoaderMode) {
      // Clear any pending animations from previous state
      if (!loaderInitialized.current) {
        clearAllAnimationTimers();
      }
      
      // Only initialize once (except for QR mode which needs fresh initialization)
      if (loaderInitialized.current && mode !== 'qrcode') {
        return;
      }
      
      dlog('[Unified] 🔄 Loader mode: starting infinite animation');
      loaderInitialized.current = true;
      shouldStopLoader.current = false; // Reset stop flag
      loaderFlipCount.current = 0; // Reset flip counter
      setLoaderComplete(false); // Reset loader complete flag
      hasMountedAndChecked.current = true; // Mark as checked
      
      // Reset refs
      leftDarknessLevel.current = 1;
      rightDarknessLevel.current = 1;
      currentCycleLevel.current = 1;
      currentFlapInCycle.current = 0;
      
      // Set initial colors
      setLeftColor(generateColor(1));
      setRightColor(generateColor(1));
      setLeftEmotion('default');
      setRightEmotion('default');
      setIsReady(false); // Not ready yet, still loading
      
      // Start infinite animation loop
      const startInfiniteAnimation = () => {
        // Check if we should stop (but only after minimum flips)
        if (shouldStopLoader.current && loaderFlipCount.current >= minFlipsBeforeTransition) {
          dlog(`[Unified] 🛑 Loader stop flag detected after ${loaderFlipCount.current} flips - exiting loop`);
          hasCompletedLoaderCycle.current = true;
          // Trigger state update to process emotions
          setTimeout(() => {
            setLoaderComplete(true);
          }, 50);
          return;
        }
        
        // Determine target level for this cycle
        // Cycle: 1 → 2 → 3 → 1
        let targetLevel;
        if (currentCycleLevel.current === 1) {
          targetLevel = 2;
        } else if (currentCycleLevel.current === 2) {
          targetLevel = 3;
        } else {
          targetLevel = 1;
        }
        
        const isLeftSide = currentFlapInCycle.current === 0;
        
        if (isLeftSide) {
          setTimeout(() => {
            // Animate left flap to target level
            setLeftColorFront(generateColor(targetLevel));
            setIsAnimatingLeft(true);
            
            setTimeout(() => {
              setLeftColor(generateColor(targetLevel));
              leftDarknessLevel.current = targetLevel;
              setTimeout(() => {
                setIsAnimatingLeft(false);
                loaderFlipCount.current++; // Increment flip counter
                dlog(`[Unified] 📊 Loader flip ${loaderFlipCount.current} complete (left)`);
              }, 50);
              
              // Move to right flap in this cycle
              currentFlapInCycle.current = 1;
              setTimeout(() => {
                startInfiniteAnimation();
              }, 300);
            }, 800);
          }, 0);
        } else {
          setTimeout(() => {
            // Animate right flap to target level
            setRightColorFront(generateColor(targetLevel));
            setIsAnimatingRight(true);
            
            setTimeout(() => {
              setRightColor(generateColor(targetLevel));
              rightDarknessLevel.current = targetLevel;
              setTimeout(() => {
                setIsAnimatingRight(false);
                loaderFlipCount.current++; // Increment flip counter
                dlog(`[Unified] 📊 Loader flip ${loaderFlipCount.current} complete (right)`);
              }, 50);
              
              // Both flaps are now at target level - cycle complete
              // Move to next cycle level
              currentCycleLevel.current = targetLevel;
              currentFlapInCycle.current = 0; // Reset to left for next cycle
              
              setTimeout(() => {
                startInfiniteAnimation();
              }, 300);
            }, 800);
          }, 0);
        }
      };
      
      startInfiniteAnimation();
      
      // Cleanup function for loader mode
      return () => {
        dlog('[Unified] 🛑 Cleaning up loader mode');
        shouldStopLoader.current = true; // Signal loop to stop
        clearAllAnimationTimers();
        loaderInitialized.current = false;
      };
    }
    
    // Reset loader flag when exiting loader mode
    if (loaderInitialized.current) {
      dlog('[Unified] 🛑 Exiting loader mode - resetting');
      shouldStopLoader.current = true; // Signal loop to stop
      loaderInitialized.current = false;
      clearAllAnimationTimers();
    }
    
    
    // ============================================
    // LOGO MODE: Emotional state based on matches
    // ============================================
    if (mode === 'logo') {
      // If still loading, wait
      if (isLoading) {
        dlog('[Unified] ⏭️  Logo mode: Still loading - waiting...', { matches: matches?.length });
        setIsReady(false);
        return;
      }
      
      // IMPORTANT: Don't process emotions until loader has completed minimum flips
      // This prevents pink/orange colors from flashing before loader runs
      if (!hasCompletedLoaderCycle.current) {
        const flipsCompleted = loaderFlipCount.current;
        const flipsRemaining = minFlipsBeforeTransition - flipsCompleted;
        dlog(`[Unified] ⏸️  Loader still running - ${flipsCompleted}/${minFlipsBeforeTransition} flips complete`);
        setIsReady(false);
        return;
      }
      
      dlog('[Unified] ✅ Loader cycle complete - processing emotions now');
      
      // Default colors for scoring screen
      const DEFAULT_COLOR = '#FFB84D';  // Gold-orange (denotes winning/positive state)
      const DEFAULT_EMOTION = 'joyful';
      
      // If no matches, show default state
      if (!matches || matches.length === 0) {
        const noMatchesKey = 'no-matches-state';

        // Skip first render only if the loader is still running, to avoid
        // a color flash before the loader animation begins. Once the loader
        // has completed, fall through to render the default happy state
        // (otherwise brand-new users with no matches see a blank logo).
        if (!hasInitialized.current && prevMatchesRef.current === null && !hasCompletedLoaderCycle.current) {
          dlog('[Unified] ⏭️  First render with no matches before loader - skipping to avoid flash');
          return;
        }
        
        if (prevMatchesRef.current === noMatchesKey) {
          dlog('[Unified] ⏭️  Already in no-matches state');
          return;
        }
        
        if (prevMatchesRef.current !== noMatchesKey) {
          clearAllAnimationTimers();
        }
        
        dlog('[Unified] 🎯 No matches - showing default happy state');
        prevMatchesRef.current = noMatchesKey;
        setIsReady(true);
        
        // Set front layer with default colors
        setLeftColorFront(DEFAULT_COLOR);
        setLeftEmotionFront(DEFAULT_EMOTION);
        setRightColorFront(DEFAULT_COLOR); // Use same color as left
        setRightEmotionFront(DEFAULT_EMOTION);
        
        // Trigger animation
        const logoTimer1 = setTimeout(() => {
          setIsAnimatingLeft(true);
          
          const logoTimer2 = setTimeout(() => {
            setLeftColor(DEFAULT_COLOR);
            setLeftEmotion(DEFAULT_EMOTION);
            const logoTimer3 = setTimeout(() => {
              setIsAnimatingLeft(false);
            }, 50);
            animationTimers.current.push(logoTimer3);
          }, 800);
          animationTimers.current.push(logoTimer2);
          
          const logoTimer4 = setTimeout(() => {
            setIsAnimatingRight(true);
            
            const logoTimer5 = setTimeout(() => {
              setRightColor(DEFAULT_COLOR); // Use same color as left
              setRightEmotion(DEFAULT_EMOTION);
              const logoTimer6 = setTimeout(() => {
                setIsAnimatingRight(false);
              }, 50);
              animationTimers.current.push(logoTimer6);
            }, 800);
            animationTimers.current.push(logoTimer5);
          }, 150);
          animationTimers.current.push(logoTimer4);
        }, 50);
        animationTimers.current.push(logoTimer1);
        
        return;
      }
      
      // Clear the no-match timeout
      if (noMatchTimeout.current) {
        clearTimeout(noMatchTimeout.current);
        noMatchTimeout.current = null;
      }
      
      dlog('[Unified] 📊 Processing matches:', matches.length);

      // Diff key for deciding whether to re-run the emotion engine and
      // re-animate the flaps. Only includes data that actually affects
      // emotion output:
      //   - team names + colors → identity emotion (color clustering, "my team")
      //   - setsWon              → performance emotion (only changes on set completion)
      //   - status               → playing → final transition
      //
      // Intentionally EXCLUDES the current set's running score
      // (teamAScore / teamBScore) and the mutable `sets` array.
      // Those tick on every score tap and would re-fire the animation
      // on every Firebase update — which manifested as multi-device
      // flashing on the viewer (#27).
      const currentMatchData = JSON.stringify(matches.map(match => ({
        id: match.id,
        teamA: match.teamA,
        teamB: match.teamB,
        teamAColor: match.teamAColor,
        teamBColor: match.teamBColor,
        setsWon: match.setsWon,
        status: match.status,
      })));
      
      // Check if match data has actually changed
      if (prevMatchesRef.current === currentMatchData) {
        dlog('[Unified] ⏭️  Skipping emotion calculation - no changes');
        return;
      }
      
      dlog('[Unified] ✨ Match data changed! Animating...');
      clearAllAnimationTimers();
      hasInitialized.current = true;
      hasMountedAndChecked.current = true; // Mark as checked
      prevMatchesRef.current = currentMatchData;
      
      // ====================================================
      // EMOTION CALCULATION - Unified: both flaps same color and emotion
      // ====================================================
      const myTeam = inferMyTeam(matches);
      const performanceEmotion = calculatePerformanceEmotion(matches);
      const mostUsedColor = getMostUsedColor(matches, myTeam);
      
      // Both flaps: Use favorite team color and same emotion face
      const flapColor = mostUsedColor || DEFAULT_COLOR;
      const flapEmotion = performanceEmotion || DEFAULT_EMOTION;
      
      setIsReady(true);
      
      dlog('[Unified] 🎨 Color Debug:', { 
        myTeam,
        mostUsedColor, 
        performanceEmotion, 
        flapColor, 
        flapEmotion 
      });
      
      // Set front layer data
      setLeftColorFront(flapColor);
      setLeftEmotionFront(flapEmotion);
      setRightColorFront(flapColor);
      setRightEmotionFront(flapEmotion);
      
      // Trigger animations
      const matchTimer1 = setTimeout(() => {
        setIsAnimatingLeft(true);
        
        const matchTimer2 = setTimeout(() => {
          setLeftColor(flapColor);
          setLeftEmotion(flapEmotion);
          
          const matchTimer3 = setTimeout(() => {
            setIsAnimatingLeft(false);
          }, 50);
          animationTimers.current.push(matchTimer3);
        }, 800);
        animationTimers.current.push(matchTimer2);
        
        // Right flap animation with 150ms delay
        const matchTimer4 = setTimeout(() => {
          setIsAnimatingRight(true);
          
          const matchTimer5 = setTimeout(() => {
            setRightColor(flapColor);
            setRightEmotion(flapEmotion);
            
            const matchTimer6 = setTimeout(() => {
              setIsAnimatingRight(false);
            }, 50);
            animationTimers.current.push(matchTimer6);
          }, 800);
          animationTimers.current.push(matchTimer5);
        }, 150);
        animationTimers.current.push(matchTimer4);
      }, 50);
      animationTimers.current.push(matchTimer1);
    }
    
    // Cleanup
    return () => {
      if (noMatchTimeout.current) {
        clearTimeout(noMatchTimeout.current);
        noMatchTimeout.current = null;
      }
    };
  }, [mode, matches, matchId, isLoading, loaderComplete]);

  // Determine if we should hide the gap (for QR code mode)
  const shouldHideGap = mode === 'qrcode' && isReady;

  // SVG faces are now used instead of expressions

  // Generate QR code URL
  const qrCodeUrl = matchId ? `${window.location.origin}/match/${matchId}` : '';

  dlog('[Unified] 🎭 Render state:', { 
    mode,
    isReady, 
    isLoading, 
    leftColor, 
    rightColor, 
    matchesCount: matches?.length,
    matchId,
    hasMountedAndChecked: hasMountedAndChecked.current
  });

  // Don't render anything until we've checked what mode we should be in
  // This prevents flash of colors before loader starts
  if (!hasMountedAndChecked.current && mode !== 'loader') {
    dlog('[Unified] ⏸️  Waiting for initial mode check...', { mode, hasMountedAndChecked: hasMountedAndChecked.current });
    return null;
  }

  return (
    <div 
      className={`dynamic-flapper-unified ${mode}-mode size-${size}`}
      style={{ 
        width: dimension, 
        height: dimension,
        display: 'inline-block',
      }}
    >
      <div className={`flapper-container ${shouldHideGap ? 'no-gap' : ''}`}>
        <div className="flapper-tile">
          {/* Left back flap */}
          <div className="flap back" style={{ backgroundColor: leftColor }}>
            {(mode === 'loader' || (isLoading && mode !== 'qrcode')) ? (
              <div className="loader-indicator" />
            ) : mode === 'qrcode' && isReady && !isLoading ? (
              <div className="qr-container qr-left">
                <QRCode value={qrCodeUrl} size={dimension * 2} level="M" />
              </div>
            ) : mode === 'logo' && isReady && !isLoading && hasCompletedLoaderCycle.current ? (
              <FaceHalf 
                emotion={leftEmotion} 
                side="left" 
                size={faceSize}
                className="face-half-back"
              />
            ) : (mode === 'logo' && !hasCompletedLoaderCycle.current) ? (
              <div className="loader-indicator" />
            ) : null}
          </div>
          
          {/* Left front flap */}
          <div className={`flap front ${isAnimatingLeft ? 'flipping' : ''}`} style={{ backgroundColor: leftColorFront }}>
            {(mode === 'loader' || (isLoading && mode !== 'qrcode')) ? (
              <div className="loader-indicator" />
            ) : mode === 'qrcode' && isReady && !isLoading ? (
              <div className="qr-container qr-left">
                <QRCode value={qrCodeUrl} size={dimension * 2} level="M" />
              </div>
            ) : mode === 'logo' && !isLoading && hasCompletedLoaderCycle.current ? (
              <FaceHalf 
                emotion={leftEmotionFront} 
                side="left" 
                size={faceSize}
                className="face-half-front"
              />
            ) : (mode === 'logo' && !hasCompletedLoaderCycle.current) ? (
              <div className="loader-indicator" />
            ) : null}
          </div>
        </div>
        
        <div className="flapper-tile">
          {/* Right back flap */}
          <div className="flap back" style={{ backgroundColor: rightColor }}>
            {(mode === 'loader' || (isLoading && mode !== 'qrcode')) ? (
              <div className="loader-indicator" />
            ) : mode === 'qrcode' && isReady && !isLoading ? (
              <div className="qr-container qr-right">
                <QRCode value={qrCodeUrl} size={dimension * 2} level="M" />
              </div>
            ) : mode === 'logo' && isReady && !isLoading && hasCompletedLoaderCycle.current ? (
              <FaceHalf 
                emotion={rightEmotion} 
                side="right" 
                size={faceSize}
                className="face-half-back"
              />
            ) : (mode === 'logo' && !hasCompletedLoaderCycle.current) ? (
              <div className="loader-indicator" />
            ) : null}
          </div>
          
          {/* Right front flap */}
          <div className={`flap front ${isAnimatingRight ? 'flipping' : ''}`} style={{ backgroundColor: rightColorFront }}>
            {(mode === 'loader' || (isLoading && mode !== 'qrcode')) ? (
              <div className="loader-indicator" />
            ) : mode === 'qrcode' && isReady && !isLoading ? (
              <div className="qr-container qr-right">
                <QRCode value={qrCodeUrl} size={dimension * 2} level="M" />
              </div>
            ) : mode === 'logo' && !isLoading && hasCompletedLoaderCycle.current ? (
              <FaceHalf 
                emotion={rightEmotionFront} 
                side="right" 
                size={faceSize}
                className="face-half-front"
              />
            ) : (mode === 'logo' && !hasCompletedLoaderCycle.current) ? (
              <div className="loader-indicator" />
            ) : null}
          </div>
        </div>
      </div>

      {/* Mouth is now included in the SVG faces, so no separate overlay needed */}
      
      {/* QR Code overlay text - qrcode mode only */}
      {mode === 'qrcode' && isReady && (
        <div className="qr-overlay-text">
          Scan to follow the score
        </div>
      )}
    </div>
  );
};

export default DynamicFlapperUnified;
